import { useRef } from 'react'
import { t } from '../i18n'
import { cacheBustedUrl } from './cache-bust'

/** How long the cashier must hold the badge before we offer the cache-clearing reload. */
const HOLD_MS = 2000

/** Drop every cache the web view might be serving stale assets from (Service Worker + Cache
 * Storage), then navigate to a URL it cannot have cached. Best-effort: a missing/blocked cache API
 * must not stop the reload. */
async function reloadFresh() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister()))
    }
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch {
    // ignore — navigate regardless so the user isn't stuck on a stale build
  }
  // `replace`, not `assign`: the stale URL must not stay one back-gesture away.
  window.location.replace(cacheBustedUrl(window.location.href, Date.now()))
}

/** Tiny build-identity badge pinned to the bottom-left of every screen, so we can read which
 * version/commit is live on a device when something looks off. `mix-blend-difference` keeps it
 * legible over both the white scan screen and the blue profile.
 *
 * Hold it for {@link HOLD_MS} to be prompted to reload and empty the cache — the cashier's escape
 * hatch when a device is stuck on an old build after a deploy.
 *
 * The prompt is a native `<dialog>` driven imperatively (`showModal`/`close`), NOT `window.confirm`:
 * the LSK POS web view suppresses native dialogs, so the hold appeared to "do nothing". */
export function VersionBadge() {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)

  function clearHold() {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }

  function startHold() {
    clearHold()
    timer.current = setTimeout(() => {
      clearHold()
      dialogRef.current?.showModal()
    }, HOLD_MS)
  }

  return (
    <>
      <button
        type="button"
        onPointerDown={startHold}
        onPointerUp={clearHold}
        onPointerLeave={clearHold}
        onPointerCancel={clearHold}
        onContextMenu={(e) => e.preventDefault()}
        style={{ touchAction: 'none' }}
        title={`Built ${__BUILD_TIME__} — hold to check for an update`}
        aria-label={`Version ${__APP_VERSION__}, build ${__BUILD_SHA__}. Hold to reload and empty cache.`}
        // Padding enlarges the touch target so the long-press registers, while the text stays tiny.
        // Press feedback is pure CSS (`active:`) so holding doesn't trigger a re-render.
        className="fixed bottom-0 left-0 z-[60] select-none p-2 font-mono text-[10px] leading-none text-white opacity-60 mix-blend-difference transition-opacity active:opacity-100"
      >
        v{__APP_VERSION__}·{__BUILD_SHA__}
      </button>

      <dialog
        ref={dialogRef}
        // Named by its own heading — a modal with no accessible name is announced as just "dialog".
        aria-labelledby="version-badge-update-title"
        // `m-auto` restores the browser's default modal centering — Tailwind's preflight zeroes
        // margin on every element, which otherwise pins the dialog to the top-left.
        className="m-auto max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl backdrop:bg-black/50"
      >
        <h2 id="version-badge-update-title" className="text-lg font-semibold text-slate-800">
          {t('updateTitle')}
        </h2>
        <p className="mt-2 text-sm text-slate-500">{t('updateBody')}</p>
        <p className="mt-1 font-mono text-[11px] text-slate-400">
          {t('updateCurrent')}: v{__APP_VERSION__}·{__BUILD_SHA__}
        </p>
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={() => dialogRef.current?.close()}
            className="flex-1 rounded-xl border border-slate-300 px-4 py-3 text-base font-medium text-slate-600"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={() => void reloadFresh()}
            className="flex-1 rounded-xl bg-brand px-4 py-3 text-base font-semibold text-white"
          >
            {t('update')}
          </button>
        </div>
      </dialog>
    </>
  )
}
